import { create } from 'zustand';

interface GlobalLoadingState {
  isActive: boolean;
  message: string | null;
  source?: string; // Track which component set the loading state
}

export type LoadingStage = 'css' | 'auth' | 'cache' | 'initial-dashboard' | 'dashboard' | 'logout' | 'complete';

export interface MasterLoadingState {
  isActive: boolean;
  stage: LoadingStage;
  message: string | null;
  source: string;
}

interface UiState {
  // NEW: Master loading state - single source of truth
  masterLoadingState: MasterLoadingState;
  
  // LEGACY: Keep for backward compatibility during transition
  globalLoadingStatus: GlobalLoadingState;
  hasCompletedInitialLoad: boolean; // Track if app has completed its first load
  cacheRestorationComplete: boolean; // Track if cache restoration is complete
  isDashboardDataLoading: boolean; // Track if dashboard is currently loading initial data
  
  // NEW: Dashboard real-time updating state (for SSE optimization)
  isDashboardUpdating: boolean; // Track if dashboard is being updated via SSE
  dashboardLastUpdate: number; // Timestamp of last dashboard update
  dashboardUpdateSource: 'sse' | 'manual' | 'initial'; // Source of last update
  
  // NEW: Master loading actions
  setMasterLoading: (isActive: boolean, stage: LoadingStage, message?: string | null, source?: string) => void;
  
  // LEGACY: Keep for backward compatibility during transition
  setGlobalLoading: (isActive: boolean, message?: string | null, source?: string) => void;
  setInitialLoadComplete: () => void; // Mark initial load as complete
  setCacheRestorationComplete: () => void; // Mark cache restoration as complete
  setDashboardDataLoading: (isLoading: boolean) => void; // Track dashboard data loading state
  
  // NEW: Dashboard real-time updating actions
  setDashboardUpdating: (isUpdating: boolean, source?: 'sse' | 'manual' | 'initial') => void; // Track dashboard updating state
}

export const useUiStore = create<UiState>((set) => ({
  // NEW: Master loading state - initialized to CSS stage
  masterLoadingState: {
    isActive: true,
    stage: 'css',
    message: 'Starting application...',
    source: 'uiStore-init',
  },
  
  // LEGACY: Keep for backward compatibility during transition
  globalLoadingStatus: {
    isActive: false,
    message: null,
    source: undefined,
  },
  hasCompletedInitialLoad: false, // Reset on every app restart - not persisted
  cacheRestorationComplete: false, // Reset on every app restart - not persisted
  isDashboardDataLoading: false, // Track if dashboard is loading initial data
  
  // NEW: Dashboard real-time updating state (for SSE optimization)
  isDashboardUpdating: false, // Track if dashboard is being updated via SSE
  dashboardLastUpdate: 0, // Timestamp of last dashboard update
  dashboardUpdateSource: 'initial', // Source of last update
  
  // NEW: Master loading actions
  setMasterLoading: (isActive, stage, message = null, source = 'unknown') => {
    set(() => ({
      masterLoadingState: {
        isActive,
        stage,
        message: isActive ? (message || getDefaultMessageForStage(stage)) : null,
        source,
      },
    }));
  },
  
  // LEGACY: Keep for backward compatibility during transition
  setGlobalLoading: (isActive, message = null, source = undefined) => {
    set(() => {
      if (isActive) {
        // When activating, set the message. If no message provided, use a default.
        return { 
          globalLoadingStatus: { 
            isActive: true, 
            message: message || 'Loading...', 
            source 
          } 
        };
      } else {
        // When deactivating, always clear the message and source.
        return { 
          globalLoadingStatus: { 
            isActive: false, 
            message: null, 
            source: undefined 
          } 
        };
      }
    });
  },
  setInitialLoadComplete: () => {
    set(() => ({ hasCompletedInitialLoad: true }));
  },
  setCacheRestorationComplete: () => {
    set(() => ({ cacheRestorationComplete: true }));
  },
  setDashboardDataLoading: (isLoading: boolean) => {
    set(() => ({ isDashboardDataLoading: isLoading }));
  },
  
  // NEW: Dashboard real-time updating actions
  setDashboardUpdating: (isUpdating: boolean, source: 'sse' | 'manual' | 'initial' = 'sse') => {
    set(() => ({
      isDashboardUpdating: isUpdating,
      dashboardLastUpdate: isUpdating ? Date.now() : 0,
      dashboardUpdateSource: source
    }));
  },
}));

// Helper function for default messages
function getDefaultMessageForStage(stage: LoadingStage): string {
  switch (stage) {
    case 'css':
      return 'Starting application...';
    case 'auth':
      return 'Authenticating...';
    case 'cache':
      return 'Restoring data...';
    case 'initial-dashboard':
      return 'Loading dashboard...';
    case 'dashboard':
      return 'Refreshing data...';
    case 'complete':
      return '';
    default:
      return 'Loading...';
  }
}